import { Modal, Select, Space, Tabs, Tooltip, Typography } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";
import { DataTable, useTableParams } from "../../components/DataTable";
import { useCharacterScope } from "../../services/character";
import { fmtCents, fmtNumber, RelativeTime, StatusTag } from "../../components/format";

const RUN_FILTERS = ["status", "formKey", "trigger", "safetyVerdict", "untrustedOnly", "sort", "order"];
const CALL_FILTERS = ["model", "purpose"];

export function BrainPage() {
  const [tab, setTab] = useState("runs");
  return (
    <Tabs
      activeKey={tab}
      onChange={setTab}
      items={[
        { key: "runs", label: "中枢决策", children: <RunsTable /> },
        { key: "calls", label: "模型调用明细", children: <CallsTable /> },
      ]}
    />
  );
}

interface RunRow {
  id: string;
  relationshipId: string | null;
  conversationId: string | null;
  userId: string | null;
  userName: string | null;
  inputText: string | null;
  trigger: string;
  formKey: string | null;
  detectedIntent: string | null;
  safetyVerdict: string;
  contextMessageCount: number;
  toolCallRounds: number;
  totalInputTokens: number;
  totalCachedTokens: number;
  totalOutputTokens: number;
  totalCostCents: number;
  latencyMs: number;
  status: string;
  triggeredByUntrustedContent: boolean;
  callCount: number;
  createdAt: string | null;
}

function RunsTable() {
  const { searchParams, setParam } = useTableParams(RUN_FILTERS);
  const scope = useCharacterScope();

  return (
    <DataTable<RunRow>
      path="/api/v1/admin/brain/runs"
      fixedParams={scope}
      filterKeys={RUN_FILTERS}
      toolbar={
        <Space wrap>
          <Select
            allowClear
            style={{ width: 130 }}
            placeholder="状态"
            value={searchParams.get("status") ?? undefined}
            onChange={(v) => setParam("status", v)}
            options={["running", "succeeded", "failed", "blocked"].map((v) => ({ value: v, label: v }))}
          />
          <Select
            allowClear
            style={{ width: 130 }}
            placeholder="形态"
            value={searchParams.get("formKey") ?? undefined}
            onChange={(v) => setParam("formKey", v)}
            options={[
              { value: "normal", label: "normal" },
              { value: "weakened", label: "weakened 笨笨" },
            ]}
          />
          <Select
            allowClear
            style={{ width: 160 }}
            placeholder="触发源"
            value={searchParams.get("trigger") ?? undefined}
            onChange={(v) => setParam("trigger", v)}
            options={["user_message", "user_action", "schedule", "system_event"].map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <Select
            allowClear
            style={{ width: 140 }}
            placeholder="安全判定"
            value={searchParams.get("safetyVerdict") ?? undefined}
            onChange={(v) => setParam("safetyVerdict", v)}
            options={["pass", "weakened", "blocked"].map((v) => ({ value: v, label: v }))}
          />
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="仅不可信内容"
            value={searchParams.get("untrustedOnly") ?? undefined}
            onChange={(v) => setParam("untrustedOnly", v)}
            options={[{ value: "true", label: "仅含不可信外部内容" }]}
          />
          <Select
            style={{ width: 150 }}
            value={searchParams.get("sort") ?? "createdAt"}
            onChange={(v) => setParam("sort", v)}
            options={[
              { value: "createdAt", label: "按时间" },
              { value: "totalCostCents", label: "按花费" },
              { value: "latencyMs", label: "按延迟" },
              { value: "totalInputTokens", label: "按输入 token" },
            ]}
          />
        </Space>
      }
      columns={[
        {
          title: "决策",
          dataIndex: "id",
          width: 90,
          render: (v: string) => <Link to={`/brain/runs/${v}`}>{v.slice(-8)}</Link>,
        },
        {
          title: "用户",
          dataIndex: "userName",
          width: 130,
          render: (v: string | null, r: RunRow) =>
            r.userId ? <Link to={`/users/${r.userId}`}>{v || "(未命名)"}</Link> : "—",
        },
        {
          // 触发这次决策的那句话。列表只给一行，点开看全文——
          // 排障时先扫一眼说了什么，再决定要不要进详情。
          title: "用户说了什么",
          dataIndex: "inputText",
          width: 260,
          render: (v: string | null) =>
            v ? (
              <Typography.Link
                onClick={() =>
                  Modal.info({ title: "用户输入原文", content: <div className="mono">{v}</div>, width: 680 })
                }
                style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {v}
              </Typography.Link>
            ) : (
              <span style={{ color: "#bbb" }}>—</span>
            ),
        },
        { title: "状态", dataIndex: "status", width: 100, render: (v) => <StatusTag value={v} /> },
        { title: "形态", dataIndex: "formKey", width: 100, render: (v) => <StatusTag value={v} /> },
        { title: "触发", dataIndex: "trigger", width: 130 },
        { title: "安全", dataIndex: "safetyVerdict", width: 90, render: (v) => <StatusTag value={v} /> },
        { title: "意图", dataIndex: "detectedIntent", width: 120, render: (v) => v || "—" },
        { title: "上下文", dataIndex: "contextMessageCount", width: 80, align: "right" },
        { title: "工具轮次", dataIndex: "toolCallRounds", width: 90, align: "right" },
        { title: "调用", dataIndex: "callCount", width: 70, align: "right" },
        { title: "输入", dataIndex: "totalInputTokens", width: 90, align: "right", render: fmtNumber },
        {
          title: "缓存",
          dataIndex: "totalCachedTokens",
          width: 90,
          align: "right",
          render: (v: number) => (
            <span style={{ color: v === 0 ? "#B4232E" : "#2F7A4B" }}>{fmtNumber(v)}</span>
          ),
        },
        { title: "输出", dataIndex: "totalOutputTokens", width: 90, align: "right", render: fmtNumber },
        {
          title: "花费",
          dataIndex: "totalCostCents",
          width: 90,
          align: "right",
          // 迁移数据的 costCents 全是 0（旧系统没记），显示 ¥0.00 会让人以为免费。
          render: (v: number) =>
            v > 0 ? (
              fmtCents(v)
            ) : (
              <Tooltip title="旧系统未记录单次调用成本，不代表真的没花钱">
                <span style={{ color: "#bbb" }}>未记录</span>
              </Tooltip>
            ),
        },
        { title: "延迟", dataIndex: "latencyMs", width: 80, align: "right", render: (v: number) => `${v}ms` },
        {
          title: "不可信",
          dataIndex: "triggeredByUntrustedContent",
          width: 80,
          render: (v: boolean) => (v ? <StatusTag value="warning" /> : "—"),
        },
        {
          title: "会话",
          dataIndex: "conversationId",
          width: 100,
          render: (v: string | null) => (v ? <Link to={`/conversations/${v}`}>{v.slice(-8)}</Link> : "—"),
        },
        { title: "时间", dataIndex: "createdAt", width: 130, render: (v) => <RelativeTime value={v} /> },
      ]}
    />
  );
}

interface CallRow {
  id: string;
  brainRunId: string;
  round: number;
  purpose: string;
  capability: string;
  resolvedModel: string;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  costCents: number;
  latencyMs: number;
  finishReason: string | null;
  errorCode: string | null;
  retentionUntil: string | null;
  createdAt: string | null;
}

function CallsTable() {
  const { searchParams, setParam } = useTableParams(CALL_FILTERS);

  return (
    <DataTable<CallRow>
      path="/api/v1/admin/brain/calls"
      filterKeys={CALL_FILTERS}
      toolbar={
        <Space wrap>
          <Select
            allowClear
            style={{ width: 250 }}
            placeholder="模型"
            value={searchParams.get("model") ?? undefined}
            onChange={(v) => setParam("model", v)}
            options={["deepseek-v4-pro", "deepseek-v4-flash-0731", "deepseek-v4-flash", "qwen3.7-flash", "gpt-4-turbo", "gpt-4", "gpt3.5"].map(
              (v) => ({ value: v, label: v === "deepseek-v4-flash" ? `${v}（历史）` : v }),
            )}
          />
          <Select
            allowClear
            style={{ width: 180 }}
            placeholder="用途"
            value={searchParams.get("purpose") ?? undefined}
            onChange={(v) => setParam("purpose", v)}
            options={["chat", "tool_digest", "safety_classify", "prompt_compose", "diary_write"].map(
              (v) => ({ value: v, label: v }),
            )}
          />
        </Space>
      }
      columns={[
        {
          title: "决策",
          dataIndex: "brainRunId",
          width: 90,
          render: (v: string) => <Link to={`/brain/runs/${v}`}>{v.slice(-8)}</Link>,
        },
        { title: "轮次", dataIndex: "round", width: 70, align: "right" },
        { title: "用途", dataIndex: "purpose", width: 140 },
        { title: "能力 key", dataIndex: "capability", width: 180 },
        { title: "实际模型", dataIndex: "resolvedModel", width: 170 },
        { title: "输入", dataIndex: "inputTokens", width: 90, align: "right", render: fmtNumber },
        { title: "缓存", dataIndex: "cachedTokens", width: 90, align: "right", render: fmtNumber },
        { title: "输出", dataIndex: "outputTokens", width: 90, align: "right", render: fmtNumber },
        {
          title: "花费",
          dataIndex: "costCents",
          width: 90,
          align: "right",
          render: (v: number) =>
            v > 0 ? fmtCents(v) : <span style={{ color: "#bbb" }}>未记录</span>,
        },
        { title: "延迟", dataIndex: "latencyMs", width: 80, align: "right", render: (v: number) => `${v}ms` },
        { title: "结束原因", dataIndex: "finishReason", width: 120, render: (v) => v || "—" },
        { title: "错误码", dataIndex: "errorCode", width: 110, render: (v) => v || "—" },
        {
          title: "prompt 保留至",
          dataIndex: "retentionUntil",
          width: 130,
          render: (v) => <RelativeTime value={v} />,
        },
        { title: "时间", dataIndex: "createdAt", width: 130, render: (v) => <RelativeTime value={v} /> },
      ]}
    />
  );
}
